import React from "react";

import { Alert, Box, Grid, Typography } from "@mui/material";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import { LoadingSkeletons } from "./LoadingSkeletons";
import Headerbar from "../../common/Headerbar";
import Breadcrumbs from "../../common/Breadcrumbs";
import { EpisodesIcon } from "../../common/icons";

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
  bgColor?: string;
  borderColor?: string;
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  borderColor,
}: StatCardProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: "400px",
        maxHeight: "60px",
        backgroundColor: bgColor ?? "#fff",
        border: "1px solid",
        borderColor: borderColor ?? "divider",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2 },
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: bgColor ? `${color}22` : `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: bgColor ? color : "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            color: bgColor ? color : "text.primary",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

interface Stats {
  total: number;
  ready: number;
  generating: number;
}

function MainContentLayout({
  children,
  error,
  isLoading,
  renderVideoCards,
  episodeName,
  episodeId,
  handleMobileMenuToggle,
  stats,
}: {
  children: React.ReactNode;
  error: boolean;
  isLoading: boolean;
  renderVideoCards: () => React.ReactNode;
  episodeName: string;
  episodeId: string;
  handleMobileMenuToggle: () => void;
  stats: Stats;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Headerbar
        pageName={`Episode - ${episodeName}`}
        onMobileMenuClick={handleMobileMenuToggle}
        headerLeftContent={
          <Breadcrumbs
            items={[
              {
                label: "Episodes",
                path: "/episodes",
                icon: <EpisodesIcon sx={{ fontSize: 18 }} />,
              },
              {
                label: episodeName || "Episode",
                path: `/episodes/${episodeId}`,
              },
              {
                label: "Short Videos",
                path: `/episodes/${episodeId}/videos`,
                icon: <OndemandVideoIcon sx={{ fontSize: 18 }} />,
              },
            ]}
            showHome={false}
            inHeader
          />
        }
      />

      <Box
        sx={{
          flex: 1,
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          overflow: "auto",
          px: 3,
          py: 2,
        }}
      >
        {/* Stat cards */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 6 },
            mb: 2,
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <StatCard
            label="Total Shorts"
            value={stats.total}
            icon={OndemandVideoIcon}
            color="#2563eb"
          />
          <StatCard
            label="Ready"
            value={stats.ready}
            icon={CheckCircleIcon}
            color="#2E7D32"
            bgColor="#E8F5E8"
            borderColor="#4CAF50"
          />
          <StatCard
            label="Generating"
            value={stats.generating}
            icon={AutorenewIcon}
            color="#E65100"
            bgColor="#FFF3E0"
            borderColor="#FFCC02"
          />
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {children}
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: "12px", border: "1px solid #fca5a5" }}
          >
            Failed to load videos. Please try again.
          </Alert>
        )}

        <Grid container spacing={2}>
          {isLoading ? <LoadingSkeletons /> : renderVideoCards()}
        </Grid>
      </Box>
    </Box>
  );
}

export { MainContentLayout };
